import React, { useEffect, useState } from 'react'
import { Button, IconButton, InputAdornment, makeStyles, TextField } from '@material-ui/core'
import Visibility from '@material-ui/icons/Visibility'
import VisibilityOff from '@material-ui/icons/VisibilityOff'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { renameOwnLogin, updatePassword } from '../../redux/actions/dashboard'
import { setUser } from '../../shared/redux/actions/common'
import { validateTenantPass } from './SetPasswordDialog'
import DashboardSection, { BUTTON_WIDTH, FIELD_GAP } from './DashboardSection'

const isValidLogin = (login: string) => Boolean(login.trim()) && !/\s/.test(login.trim())

const useStyles = makeStyles(() => ({
    form: {
        width: '100%',
    },
    passwords: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: FIELD_GAP,
    },
    button: {
        width: BUTTON_WIDTH,
    },
}))

interface Props {
    onSuccess: (msg: string) => void
    onError: (msg: string) => void
}

export default function MyAccount({ onSuccess, onError }: Props) {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const user = useSelector((state: any) => state.login.value.user)

    const [login, setLogin] = useState<string>(user?.login ?? '')
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [showPasswords, setShowPasswords] = useState(false)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        setLogin(user?.login ?? '')
    }, [user?.login])

    const loginChanged = login.trim() !== (user?.login ?? '')
    const loginInvalid = loginChanged && !isValidLogin(login)
    const passwordFilled = currentPassword.length > 0 || newPassword.length > 0
    const newPasswordInvalid = newPassword.length > 0 && !validateTenantPass(newPassword)
    const passwordReady = Boolean(currentPassword) && validateTenantPass(newPassword)

    // da sa ulozit, ak je co ulozit a vsetko vyplnene je platne
    const canSave = (loginChanged || passwordFilled) && !loginInvalid && (!passwordFilled || passwordReady) && !saving

    const handleSave = async () => {
        if (!canSave) return
        setSaving(true)
        const done: string[] = []
        try {
            if (loginChanged) {
                try {
                    await renameOwnLogin(login.trim())
                    dispatch(setUser({ ...user, login: login.trim() }))
                    done.push(t('dashboard.loginChanged'))
                } catch {
                    onError(t('dashboard.errorSavingLogin'))
                    return
                }
            }
            if (passwordFilled) {
                try {
                    await updatePassword({ currentPassword, newPassword })
                    setCurrentPassword('')
                    setNewPassword('')
                    done.push(t('dashboard.passwordChanged'))
                } catch {
                    // backend vracia 400 aj pri nespravnom aktualnom hesle
                    onError(t('dashboard.errorUpdatingPass'))
                    return
                }
            }
            onSuccess(done.join('. '))
        } finally {
            setSaving(false)
        }
    }

    const visibilityToggle = (
        <InputAdornment position="end">
            <IconButton size="small" onClick={() => setShowPasswords(!showPasswords)}>
                {showPasswords ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
        </InputAdornment>
    )

    return (
        <DashboardSection title={t('dashboard.myAccount')}>
            <div className={classes.form}>
                <TextField
                    fullWidth
                    variant="filled"
                    size="small"
                    label={t('dashboard.login')}
                    value={login}
                    inputProps={{ maxLength: 256 }}
                    error={loginInvalid}
                    FormHelperTextProps={{ style: { marginLeft: 0, marginRight: 0 } }}
                    helperText={loginInvalid ? t('dashboard.invalidLogin') : ' '}
                    onChange={(e) => setLogin(e.target.value)}
                />
            <div className={classes.passwords}>
                <TextField
                    fullWidth
                    variant="filled"
                    size="small"
                    label={t('dashboard.currentPass')}
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    FormHelperTextProps={{ style: { marginLeft: 0, marginRight: 0 } }}
                    helperText=" "
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    InputProps={{ endAdornment: visibilityToggle }}
                />
                <TextField
                    fullWidth
                    variant="filled"
                    size="small"
                    label={t('common.newPassword')}
                    type={showPasswords ? 'text' : 'password'}
                    value={newPassword}
                    error={newPasswordInvalid}
                    FormHelperTextProps={{ style: { marginLeft: 0, marginRight: 0 } }}
                    helperText={newPasswordInvalid ? t('dashboard.passwordRules') : ' '}
                    onChange={(e) => setNewPassword(e.target.value)}
                    InputProps={{ endAdornment: visibilityToggle }}
                />
            </div>
            </div>
            <Button className={classes.button} variant="contained" color="primary" disabled={!canSave} onClick={handleSave}>
                {t('dashboard.save')}
            </Button>
        </DashboardSection>
    )
}
